'use client';

import { createContext, useContext, type ReactNode } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { Link as IntlLink } from '@/i18n/routing';
import type { ComponentProps } from 'react';

/**
 * Ссылки витрины AiSearch, знающие о русском домене.
 *
 * На aisearch.ru язык один и в адресе не нужен, а next-intl про домены
 * ничего не знает: у него язык по умолчанию `en`, поэтому в каждую
 * ссылку он подставляет `/ru`. Адрес при этом рабочий, но человек видит
 * лишний префикс, а переход идёт через редирект.
 *
 * Штатная настройка `domains` не подходит: она выбирает домен по языку, а
 * у нас русский язык живёт на обоих доменах сразу, и она сняла бы
 * префикс `/ru` заодно и на aisearch.tech.
 *
 * Поэтому признак домена приходит сверху, из layout, а ссылки внутри
 * витрины берутся отсюда.
 */
const RuDomenContext = createContext(false);

export function AisRuDomenProvider({ value, children }: { value: boolean; children: ReactNode }) {
  return <RuDomenContext.Provider value={value}>{children}</RuDomenContext.Provider>;
}

/** Домен запроса обслуживает только русскую версию. */
export function useAisRuDomen(): boolean {
  return useContext(RuDomenContext);
}

/**
 * Пропсы берутся от `next/link`, а не от ссылки next-intl: у той в типах
 * свой набор атрибутов, несовместимый по мелочи (значение `popover`), и
 * обёртка не собиралась бы. Добавляем только `locale`, ради которого
 * ссылка next-intl и нужна.
 */
type Props = ComponentProps<typeof NextLink> & { locale?: string };

/**
 * Ссылка внутри витрины.
 *
 * На русском домене отдаёт голый путь: middleware сам развернёт его в
 * русскую версию. На остальных доменах ведёт себя как обычная ссылка
 * next-intl, включая смену языка из подвала.
 */
export function AisLink({ href, locale, ...rest }: Props) {
  const ruDomen = useAisRuDomen();
  const router = useRouter();

  /*
    Предзагрузка по намерению, а не по появлению на экране (#212).

    По умолчанию Next грузит заранее каждую ссылку, попавшую в окно.
    На витрине это около сорока ссылок шапки и подвала, и замер на проде
    показал по два запроса на каждую сразу после открытия страницы. На
    хорошей связи это незаметно, на плохой эти запросы занимают канал, и
    клик человека ждёт в общей очереди.

    Теперь страница грузится, когда к ссылке потянулись: навели мышь,
    коснулись пальцем, дошли табом. Этого хватает с запасом: между
    наведением и кликом проходит 200–300 мс, а готовая страница после
    #212 отдаётся из кеша.

    Вызов обёрнут: предзагрузка это подсказка, а не обязанность, и её
    сбой не должен ломать ссылку.
  */
  const nameren = () => {
    if (typeof href !== 'string' || !href.startsWith('/')) return;
    try {
      router.prefetch(href);
    } catch {
      // Не вышло заранее, загрузится по клику.
    }
  };
  const podskazki = {
    prefetch: false as const,
    onPointerEnter: nameren,
    onTouchStart: nameren,
    onFocus: nameren,
  };

  // Явная смена языка (переключатель в подвале) обрабатывается обычной
  // ссылкой next-intl: на русском домене переключателя нет, а на .tech
  // он должен работать как раньше.
  //
  // Здесь `next/link`, а не голый `<a>`: переходы внутри витрины должны
  // остаться клиентскими, иначе каждый клик перезагружал бы страницу.
  if (ruDomen && locale == null) {
    return <NextLink href={href} {...podskazki} {...rest} />;
  }

  return <IntlLink href={href} locale={locale} {...rest} />;
}
